import { z } from 'zod';
import { geofencePolygonProblem } from './geofence-polygon';
import { defineListQuery, listFilter } from './list-query.schemas';
import { ZONE_RADIUS_METERS, zoneGeoJsonPointSchema, zoneGeoJsonPolygonSchema, type ZoneGeoJsonPoint, type ZoneGeoJsonPolygon } from './pricing.schemas';

/**
 * Geofences (gitaumoses4/chukua#89): the boundaries the platform watches, kept as our own data so changing GPS provider
 * never means redrawing them. A geofence takes its boundary from a zone (`zoneId`), or has its own: a drawn polygon, or a
 * point and a radius. Exactly one of the three.
 */
export const geofenceInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    zoneId: z.uuid().nullable().optional(),
    polygon: zoneGeoJsonPolygonSchema.nullable().optional(),
    centroid: zoneGeoJsonPointSchema.nullable().optional(),
    radiusMeters: z.number().int().min(ZONE_RADIUS_METERS.min).max(ZONE_RADIUS_METERS.max).nullable().optional(),
  })
  .strict()
  .superRefine((geofence, ctx) => {
    const hasPoint = Boolean(geofence.centroid) || geofence.radiusMeters != null;
    const sources = [Boolean(geofence.zoneId), Boolean(geofence.polygon), hasPoint].filter(Boolean).length;
    if (sources !== 1) {
      ctx.addIssue({ code: 'custom', path: ['zoneId'], message: 'Give a zone, a drawn outline, or a point with a radius, and only one of them' });
      return;
    }
    if (hasPoint && (!geofence.centroid || geofence.radiusMeters == null)) {
      ctx.addIssue({ code: 'custom', path: ['radiusMeters'], message: 'A point geofence needs both its centre and a radius' });
    }
    const problem = geofence.polygon ? geofencePolygonProblem(geofence.polygon) : null;
    if (problem) ctx.addIssue({ code: 'custom', path: ['polygon'], message: problem });
  });
export type GeofenceInput = z.infer<typeof geofenceInputSchema>;

export const GEOFENCE_STATUSES = ['active', 'inactive'] as const;
export type GeofenceStatus = (typeof GEOFENCE_STATUSES)[number];

/** `GET /geofences`. `status` is multi-value like every list filter. */
export const geofenceListQuerySchema = defineListQuery({
  sortFields: ['name', 'createdAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: { status: listFilter(z.enum(GEOFENCE_STATUSES)) },
});
export type GeofenceListQuery = z.infer<typeof geofenceListQuerySchema>;

/** Where a geofence's copy at one GPS installation stands; `lastError` is why the last attempt failed, so an admin can see a push that is not working. */
export interface GeofencePushState {
  integrationId: string;
  status: GeofencePushStatus;
  attempts: number;
  lastError: string | null;
  lastAttemptedAt: string | null;
}

/** A geofence as the API returns it. A zone-backed one carries no boundary of its own: `zoneId` says where to read it. */
export interface Geofence {
  id: string;
  name: string;
  zoneId: string | null;
  zoneName: string | null;
  polygon: ZoneGeoJsonPolygon | null;
  centroid: ZoneGeoJsonPoint | null;
  radiusMeters: number | null;
  isActive: boolean;
  /** One per GPS installation that holds geofences; empty when none does (our own evaluation covers it). */
  pushes: GeofencePushState[];
  createdAt: string;
  updatedAt: string;
}

/** Where a geofence's copy at one GPS installation stands: `pending` until the first push, `failed` with the reason kept. */
export const GEOFENCE_PUSH_STATUSES = ['pending', 'synced', 'failed'] as const;
export type GeofencePushStatus = (typeof GEOFENCE_PUSH_STATUSES)[number];

/** A truck going in (`enter`) or out (`exit`) of a geofence, and who noticed: the GPS vendor, or our own evaluation of stored positions. */
export const GEOFENCE_CROSSING_KINDS = ['enter', 'exit'] as const;
export type GeofenceCrossingKind = (typeof GEOFENCE_CROSSING_KINDS)[number];
export const GEOFENCE_CROSSING_SOURCES = ['vendor', 'server'] as const;
export type GeofenceCrossingSource = (typeof GEOFENCE_CROSSING_SOURCES)[number];

/**
 * Where a zone's watching stands, derived from the zone and its geofence (`zone-watch.policy.ts`): `starting` until the sync has made the
 * geofence, `off` for a place with its switch off, `no_shape` when there is nothing to judge a fix against.
 */
export const ZONE_WATCH_STATUSES = ['watching', 'starting', 'off', 'inactive', 'no_shape'] as const;
export type ZoneWatchStatus = (typeof ZONE_WATCH_STATUSES)[number];

/** A vendor push that has not worked (#447): which GPS installation, how often it was tried and why it failed. */
export interface ZoneFailedPush {
  integrationId: string;
  integrationName: string;
  attempts: number;
  lastError: string | null;
  lastAttemptedAt: string | null;
}

/** `GET /zones/:id/watch`: the panel's "Watching" status and the pushes that failed. */
export interface ZoneWatchView {
  status: ZoneWatchStatus;
  failedPushes: ZoneFailedPush[];
}

/** `GET /zones/:id/crossings`, a zone's recent arrivals and departures, newest first. `kind` filters to one of them. */
export const zoneCrossingListQuerySchema = defineListQuery({
  sortFields: ['deviceTime'],
  defaultSort: { sort: 'deviceTime', order: 'desc' },
  filters: { kind: listFilter(z.enum(GEOFENCE_CROSSING_KINDS)) },
});
export type ZoneCrossingListQuery = z.infer<typeof zoneCrossingListQuerySchema>;

/** One arrival (`enter`) or departure (`exit`) at a watched zone. `trip*` is null when the truck had no dispatched trip at the time. */
export interface ZoneCrossing {
  id: string;
  zoneId: string;
  zoneName: string;
  kind: GeofenceCrossingKind;
  source: GeofenceCrossingSource;
  deviceTime: string;
  truckId: string;
  truckRegistration: string;
  tripId: string | null;
  tripNumber: string | null;
}

/** The widest window the stops overview reads, and the most cells it returns: it is a histogram for drawing, not a report. */
export const GEOFENCE_STOPS_MAX_DAYS = 31;
export const GEOFENCE_STOPS_MAX_CELLS = 500;

/** `GET /geofences/stops`: where trucks stood still inside a map's bounds, to suggest where a geofence belongs. */
export const geofenceStopsQuerySchema = z
  .object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    west: z.coerce.number().min(-180).max(180),
    south: z.coerce.number().min(-90).max(90),
    east: z.coerce.number().min(-180).max(180),
    north: z.coerce.number().min(-90).max(90),
  })
  .superRefine((query, ctx) => {
    const span = Date.parse(query.to) - Date.parse(query.from);
    if (!(span > 0) || span > GEOFENCE_STOPS_MAX_DAYS * 86_400_000) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: `The window must be after its start and at most ${GEOFENCE_STOPS_MAX_DAYS} days` });
    }
    if (query.west >= query.east || query.south >= query.north) {
      ctx.addIssue({ code: 'custom', path: ['west'], message: 'The bounds must run west to east and south to north' });
    }
  });
export type GeofenceStopsQuery = z.infer<typeof geofenceStopsQuerySchema>;

export interface GeofenceStopCell {
  lng: number;
  lat: number;
  stops: number;
  trucks: number;
}
